import { useState } from "react";
import { IconButton, MenuItem, Select, Typography, Box } from "@mui/material";
import { RightOutlined, LeftOutlined } from "@ant-design/icons";

const CustomPagination = ({
  setPaginationModel,
  rowCount,
  paginationModel,
}) => {
  const [page, setPage] = useState(paginationModel.page);
  const [rowsPerPage, setRowsPerPage] = useState(paginationModel.pageSize);
  const totalCount = rowCount; // total rows
  const totalPages = Math.ceil(totalCount / rowsPerPage);
  const handleChangePage = (event, newPage) => {
    setPaginationModel((prev) => ({ ...prev, page: newPage }));
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPaginationModel((prev) => ({
      ...prev,
      page: 0,
      pageSize: parseInt(event.target.value, 10),
    }));
    setPage(0);
  };

  // Generate visible pages with ellipsis
  const getVisiblePages = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (page + 1 <= 4) pages.push(1, 2, 3, 4, 5, "...", totalPages);
      else if (page + 1 > totalPages - 4)
        pages.push(
          1,
          "...",
          totalPages - 4,
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages
        );
      else pages.push(1, "...", page, page + 1, page + 2, "...", totalPages);
    }
    return pages;
  };

  const visiblePages = getVisiblePages();

  const getFilteredArray = (rowCount) => {
    const arr = [10, 20, 30, 40, 50];
    return arr.filter((num) => num <= Math.ceil(rowCount / 10) * 10);
  };

  const options = getFilteredArray(rowCount);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "8px 16px",
        fontFamily: "sans-serif",
        width: "100%",
      }}
    >
      {/* Rows per page */}
      <Box
        sx={{ display: "flex", alignItems: "center", gap: "12px !important" }}
      >
        <Typography variant="body2" sx={{ color: "#3b5175", fontWeight: 600 }}>
          Rows per page
        </Typography>
        <Select
          value={rowsPerPage}
          onChange={handleChangeRowsPerPage}
          variant="standard"
          disableUnderline
          MenuProps={{
            PaperProps: {
              sx: {
                "& .MuiButtonBase-root.MuiMenuItem-root.Mui-selected": {
                  backgroundColor: "rgba(25, 118, 210, 0.08)",
                  width: "100% !important",
                },
                "& .MuiButtonBase-root.MuiMenuItem-root": {
                  width: "100% !important",
                },
                "& .MuiButtonBase-root.MuiMenuItem-root.Mui-selected:hover": {
                  backgroundColor: "rgba(25, 118, 210, 0.12)", // optional hover shade
                },
                "& .MuiNativeSelect-root .MuiSelect-select .MuiInputBase-input .MuiInput-input":
                  {
                    padding: "0px !important",
                    display: "flex !important",
                    justifyContent: "center !important",
                    alignItems: "center !important",
                  },
              },
            },
          }}
          sx={{
            fontSize: "14px",
            color: "#3b5175",
            fontWeight: 500,
          }}
        >
          {options.map((rows) => (
            <MenuItem key={rows} value={rows}>
              {rows}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {/* Page info and buttons */}
      <Box
        sx={{ display: "flex", alignItems: "center", gap: "4px !important" }}
      >
        <Typography
          variant="body2"
          sx={{ color: "#3b5175", marginRight: "16px" }}
        >
          {`${page * rowsPerPage + 1}-${Math.min(
            (page + 1) * rowsPerPage,
            totalCount
          )} of ${totalCount}`}
        </Typography>

        <IconButton
          onClick={() => handleChangePage(null, Math.max(page - 1, 0))}
          sx={{
            color: "#3b5175",
            cursor: page === 0 ? "no-drop !important" : "pointer !important",
          }}
        >
          <LeftOutlined
            style={{
              fontSize: "1rem",
              color: "#3b5175",
              borderRadius: "50%",
              padding: "2px",
              cursor: page === 0 ? "no-drop" : "pointer",
            }}
          />
        </IconButton>

        {visiblePages.map((p, i) =>
          p === "..." ? (
            <Typography key={i} sx={{ color: "#185edf" }}>
              ...
            </Typography>
          ) : (
            <IconButton
              key={p}
              onClick={() => handleChangePage(null, p - 1)}
              sx={{
                width: 32,
                height: 32,
                color: "#185edf",
                backgroundColor: page + 1 === p ? "#D6E4FF" : "transparent",
                "&:hover": { backgroundColor: "#E9F0FF" },
                borderRadius: "50%",
                fontWeight: 600,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: "2px",
              }}
            >
              {p}
            </IconButton>
          )
        )}

        <IconButton
          onClick={() =>
            handleChangePage(null, Math.min(page + 1, totalPages - 1))
          }
          sx={{
            color: "#3b5175",
            cursor:
              page + 1 === totalPages
                ? "no-drop !important"
                : "pointer !important",
          }}
        >
          <RightOutlined
            style={{
              fontSize: "1rem",
              color: "#3b5175",
              borderRadius: "50%",
              padding: "2px",
              cursor: page + 1 === totalPages ? "no-drop" : "pointer",
            }}
          />
        </IconButton>
      </Box>
    </Box>
  );
};

export default CustomPagination;
